/**
 * Camera Alignment Tool
 *
 * Reusable component for aligning the orthographic camera to a line defined by two points.
 * Shows visual feedback including aligned crosses, connecting line, rotation arc, and degree label.
 */

import * as THREE from 'three';
import { Viewer } from '../viewer/viewer';

export interface CameraAlignmentOptions {
  crossColor?: number;
  arcColor?: number;
  lineWidth?: number;
  arcLineWidth?: number;
  arcRadiusScale?: number; // Percentage of line length (0-1)
  minArcRadius?: number;
  maxArcRadius?: number;
  labelScaleDivisor?: number; // For tuning label size relative to frustum
}

export class CameraAlignmentTool {
  private viewer: Viewer;
  private options: Required<CameraAlignmentOptions>;

  // Alignment state
  private isActive = false;
  private startPoint: THREE.Vector3 | null = null;
  private endPoint: THREE.Vector3 | null = null;

  // Screen positions (in pixels, Y down) - stored separately for accurate angle calculation
  private startScreenPos: THREE.Vector2 | null = null;
  private endScreenPos: THREE.Vector2 | null = null;

  // Visual elements
  private visuals: {
    startCross?: THREE.LineSegments;
    endCross?: THREE.LineSegments;
    line?: THREE.Line;
    arc?: THREE.Line;
    angleLabel?: THREE.Sprite;
  } = {};

  // Camera update handler
  private cameraUpdateHandler: (() => void) | null = null;

  constructor(viewer: Viewer, options: CameraAlignmentOptions = {}) {
    this.viewer = viewer;

    // Set default options
    this.options = {
      crossColor: 0x702ecf, // Brand purple
      arcColor: 0x702ecf, // Brand purple
      lineWidth: 6,
      arcLineWidth: 6,
      arcRadiusScale: 0.2, // 20% of line length
      minArcRadius: 0.2,
      maxArcRadius: 2.0,
      labelScaleDivisor: 15,
      ...options,
    };
  }

  /**
   * Start alignment mode with the first point
   * @param worldPosition - The world position of the start point
   * @param screenPosition - Optional screen position in pixels (Y down). If not provided, will be calculated from world position.
   */
  public start(worldPosition: THREE.Vector3, screenPosition?: THREE.Vector2): void {
    this.isActive = true;
    this.startPoint = worldPosition.clone();
    this.endPoint = null;
    this.startScreenPos = screenPosition?.clone() ?? this.worldToScreenPixels(worldPosition);
    this.endScreenPos = null;

    // Add camera update listener to reproject visuals when camera moves
    this.cameraUpdateHandler = () => {
      if (this.isActive) {
        this.updateVisuals();
      }
    };

    this.viewer.addEventListener('camera_changed', this.cameraUpdateHandler);
    this.viewer.addEventListener('controls_changed', this.cameraUpdateHandler);

    this.updateVisuals();
  }

  /**
   * Update the end point (called on mouse move)
   * @param worldPosition - The world position of the end point
   * @param screenPosition - Optional screen position in pixels (Y down). If not provided, will be calculated from world position.
   */
  public updateEndPoint(worldPosition: THREE.Vector3, screenPosition?: THREE.Vector2): void {
    if (!this.isActive || !this.startPoint) return;

    this.endPoint = worldPosition.clone();
    this.endScreenPos = screenPosition?.clone() ?? this.worldToScreenPixels(worldPosition);
    this.updateVisuals();
  }

  /**
   * Complete the alignment - rotate camera and clear visuals
   * Uses screen-space coordinates for accurate angle calculation
   * @param endWorldPosition - The world position of the end point
   * @param screenPosition - Optional screen position in pixels (Y down). If not provided, will be calculated.
   */
  public complete(endWorldPosition: THREE.Vector3, screenPosition?: THREE.Vector2): void {
    if (!this.startPoint || !this.startScreenPos) return;

    // Set end positions
    this.endPoint = endWorldPosition.clone();
    this.endScreenPos = screenPosition?.clone() ?? this.worldToScreenPixels(endWorldPosition);

    // Calculate angle directly from screen pixel coordinates
    const screenAngle = this.getScreenSpaceLineAngle();

    // The angle difference is how much we need to rotate to align the line with screen horizontal
    // Screen horizontal is 0 radians (pointing right)
    // In screen pixels (Y down), positive angle is clockwise
    let angleDiff = screenAngle;

    // Normalize for shortest path [-PI, PI]
    while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
    while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

    // Add this angle difference to the current roll
    // Roll is applied such that positive roll rotates the view counter-clockwise
    // So to align a line at angle X with horizontal, we subtract the angle
    const currentRoll = this.viewer.scene.view.roll;
    let newRoll = currentRoll + angleDiff; // Note: changed from - to + because screen Y is down

    // Normalize to [-PI, PI]
    while (newRoll > Math.PI) newRoll -= 2 * Math.PI;
    while (newRoll < -Math.PI) newRoll += 2 * Math.PI;

    // Set the camera roll
    this.viewer.scene.view.roll = newRoll;

    // Save the rotation to the camera settings manager for persistence
    if (this.viewer.cameraSettingsManager) {
      this.viewer.cameraSettingsManager.saveCurrentRotation('alignment_tool');
    }

    // Clear and deactivate
    this.cancel();
  }

  /**
   * Cancel alignment mode and clear all visuals
   */
  public cancel(): void {
    this.clearVisuals();
    this.isActive = false;
    this.startPoint = null;
    this.endPoint = null;
    this.startScreenPos = null;
    this.endScreenPos = null;

    // Remove camera update listener
    if (this.cameraUpdateHandler) {
      this.viewer.removeEventListener('camera_changed', this.cameraUpdateHandler);
      this.viewer.removeEventListener('controls_changed', this.cameraUpdateHandler);
      this.cameraUpdateHandler = null;
    }
  }

  /**
   * Check if alignment is active
   */
  public isAlignmentActive(): boolean {
    return this.isActive;
  }

  /**
   * Get the current start point
   */
  public getStartPoint(): THREE.Vector3 | null {
    return this.startPoint;
  }

  /**
   * Calculate the screen-space angle of the line between start and end points
   * Uses stored screen pixel coordinates (Y down) for accurate calculation
   * Returns angle in radians where 0 = right, positive = clockwise (screen Y is down)
   */
  private getScreenSpaceLineAngle(): number {
    if (!this.startScreenPos || !this.endScreenPos) return 0;

    // Screen pixel coordinates: X increases right, Y increases DOWN
    const screenDx = this.endScreenPos.x - this.startScreenPos.x;
    const screenDy = this.endScreenPos.y - this.startScreenPos.y;

    // atan2 with Y down: positive angle = clockwise from right
    return Math.atan2(screenDy, screenDx);
  }

  /**
   * Convert a screen-space pixel offset (relative to a world point) back to world coordinates
   * Screen space: X increases right, Y increases DOWN (standard screen coordinates)
   */
  private screenOffsetToWorld(referenceWorldPos: THREE.Vector3, pixelOffsetX: number, pixelOffsetY: number): THREE.Vector3 {
    const camera = this.viewer.scene.getActiveCamera();
    const domElement = this.viewer.renderer.domElement;

    // Project the reference point to NDC
    const refNdc = referenceWorldPos.clone().project(camera);

    // Convert pixel offset to NDC offset
    // NDC: X goes -1 to 1 (left to right), Y goes -1 to 1 (bottom to top)
    // Screen: X goes 0 to width (left to right), Y goes 0 to height (top to bottom)
    const ndcOffsetX = (pixelOffsetX / domElement.clientWidth) * 2;
    const ndcOffsetY = -(pixelOffsetY / domElement.clientHeight) * 2; // Negate Y because screen Y is flipped vs NDC Y

    const targetNdc = new THREE.Vector3(
      refNdc.x + ndcOffsetX,
      refNdc.y + ndcOffsetY,
      refNdc.z, // Keep the same depth
    );

    // Unproject back to world space
    return targetNdc.unproject(camera);
  }

  /**
   * Get the screen position of a world point in pixels (from top-left)
   * Screen space: X increases right, Y increases DOWN
   */
  private worldToScreenPixels(worldPos: THREE.Vector3): THREE.Vector2 {
    const camera = this.viewer.scene.getActiveCamera();
    const domElement = this.viewer.renderer.domElement;

    const ndc = worldPos.clone().project(camera);
    return new THREE.Vector2(
      ((ndc.x + 1) / 2) * domElement.clientWidth,
      ((1 - ndc.y) / 2) * domElement.clientHeight, // NDC Y is up, screen Y is down
    );
  }

  /**
   * Update all visual elements
   */
  private updateVisuals(): void {
    if (!this.startPoint) return;

    // Clear existing visuals
    this.clearVisuals();

    // If we have an end point (mouse is moving), create the full visuals
    if (this.endPoint) {
      // Calculate angle of the line in screen space (works for any camera orientation)
      const lineAngle = this.getScreenSpaceLineAngle();

      // Create aligned cross at start point
      this.visuals.startCross = this.createAlignedCross(this.startPoint, lineAngle);
      this.viewer.scene.scene.add(this.visuals.startCross);

      // Create aligned cross at end point
      this.visuals.endCross = this.createAlignedCross(this.endPoint, lineAngle);
      this.viewer.scene.scene.add(this.visuals.endCross);

      // Create line between points
      this.createConnectingLine();

      // Create arc showing camera rotation angle
      this.createAlignmentArc();
    } else {
      // Just show a cross at the start point (no alignment yet)
      this.visuals.startCross = this.createAlignedCross(this.startPoint, 0);
      this.viewer.scene.scene.add(this.visuals.startCross);
    }
  }

  /**
   * Create a cross aligned with the line direction in screen space
   * Projects screen-space offsets back to world coordinates
   *
   * screenAngle is in screen pixel space (Y down): 0 = right, positive = clockwise
   */
  private createAlignedCross(worldPos: THREE.Vector3, screenAngle: number, sizePixels = 20): THREE.LineSegments {
    const geometry = new THREE.BufferGeometry();

    // screenAngle is already in screen pixel space (Y down)
    const cos = Math.cos(screenAngle);
    const sin = Math.sin(screenAngle);

    // Get world positions for each endpoint of the cross
    // Along direction (aligned with the line) - in screen pixel space (Y down)
    const alongNeg = this.screenOffsetToWorld(worldPos, -sizePixels * cos, -sizePixels * sin);
    const alongPos = this.screenOffsetToWorld(worldPos, sizePixels * cos, sizePixels * sin);

    // Perpendicular direction (90 degrees rotated clockwise in screen space)
    const perpNeg = this.screenOffsetToWorld(worldPos, sizePixels * sin, -sizePixels * cos);
    const perpPos = this.screenOffsetToWorld(worldPos, -sizePixels * sin, sizePixels * cos);

    const positions = new Float32Array([
      // First line (along the alignment direction)
      alongNeg.x,
      alongNeg.y,
      alongNeg.z,
      alongPos.x,
      alongPos.y,
      alongPos.z,
      // Second line (perpendicular to alignment direction)
      perpNeg.x,
      perpNeg.y,
      perpNeg.z,
      perpPos.x,
      perpPos.y,
      perpPos.z,
    ]);

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.LineBasicMaterial({
      color: this.options.crossColor,
      linewidth: this.options.lineWidth,
      depthTest: false,
      depthWrite: false,
    });

    const cross = new THREE.LineSegments(geometry, material);
    cross.renderOrder = 999;

    return cross;
  }

  /**
   * Create connecting line between start and end points
   * Uses local coordinates to prevent jitter at high elevations (400m-800m)
   */
  private createConnectingLine(): void {
    if (!this.startPoint || !this.endPoint) return;

    // Use local coordinates: position line at start point, end point is relative
    const lineGeometry = new THREE.BufferGeometry();
    const relativeEnd = new THREE.Vector3().subVectors(this.endPoint, this.startPoint);
    const positions = new Float32Array([
      0,
      0,
      0, // Start at origin (local coords)
      relativeEnd.x,
      relativeEnd.y,
      relativeEnd.z, // End relative to start
    ]);
    lineGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const lineMaterial = new THREE.LineBasicMaterial({
      color: this.options.crossColor,
      linewidth: this.options.lineWidth - 1,
      depthTest: false, // Prevent z-fighting at high elevations
      depthWrite: false,
    });

    this.visuals.line = new THREE.Line(lineGeometry, lineMaterial);
    this.visuals.line.position.copy(this.startPoint); // Position at start point
    this.visuals.line.renderOrder = 999;
    this.viewer.scene.scene.add(this.visuals.line);
  }

  /**
   * Create arc visualization showing rotation angle
   * Projects screen-space arc back to world coordinates
   *
   * The arc shows the angle from screen horizontal (right) to the line direction
   * All angles are in screen pixel space (Y down): 0 = right, positive = clockwise
   */
  private createAlignmentArc(): void {
    if (!this.startPoint || !this.startScreenPos || !this.endScreenPos) return;

    // Get the screen-space angle of the line (already in screen pixel space, Y down)
    const lineAngle = this.getScreenSpaceLineAngle();

    // The angle we need to show - this is the angle from horizontal to the line
    let angleDiff = lineAngle;

    // Normalize for shortest path [-PI, PI]
    while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
    while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

    // Calculate line length in screen pixels for scaling the arc radius
    const lineLengthPixels = this.startScreenPos.distanceTo(this.endScreenPos);

    // Scale arc radius based on line length (in pixels)
    const arcRadiusPixels = Math.max(30, Math.min(100, lineLengthPixels * this.options.arcRadiusScale));

    // Create arc geometry by projecting screen-space points to world
    const segments = 32;
    const arcPositions: number[] = [];

    // Arc starts at screen horizontal (0 radians) and sweeps to the line angle
    // In screen pixel space (Y down), positive angles go clockwise
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const screenPixelAngle = t * angleDiff;

      // Calculate screen pixel offset from start point
      const screenOffsetX = Math.cos(screenPixelAngle) * arcRadiusPixels;
      const screenOffsetY = Math.sin(screenPixelAngle) * arcRadiusPixels;

      // Project back to world
      const worldPos = this.screenOffsetToWorld(this.startPoint, screenOffsetX, screenOffsetY);
      arcPositions.push(worldPos.x, worldPos.y, worldPos.z);
    }

    const arcGeometry = new THREE.BufferGeometry();
    arcGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(arcPositions), 3));

    const arcMaterial = new THREE.LineBasicMaterial({
      color: this.options.arcColor,
      linewidth: this.options.arcLineWidth,
      depthTest: false,
      depthWrite: false,
    });

    this.visuals.arc = new THREE.Line(arcGeometry, arcMaterial);
    this.visuals.arc.renderOrder = 999;
    this.viewer.scene.scene.add(this.visuals.arc);

    // Add text label showing degrees at the arc's midpoint
    const degrees = Math.abs((angleDiff * 180) / Math.PI);
    const midScreenPixelAngle = angleDiff / 2;
    const labelRadiusPixels = arcRadiusPixels * 1.3;

    // Calculate label position by projecting screen-space offset to world
    const labelScreenOffsetX = Math.cos(midScreenPixelAngle) * labelRadiusPixels;
    const labelScreenOffsetY = Math.sin(midScreenPixelAngle) * labelRadiusPixels;
    const labelPos = this.screenOffsetToWorld(this.startPoint, labelScreenOffsetX, labelScreenOffsetY);

    this.createAngleLabel(labelPos, `${degrees.toFixed(1)}°`);
  }

  /**
   * Create a text label for the angle display
   */
  private createAngleLabel(position: THREE.Vector3, text: string): void {
    // Create a canvas for the text with higher resolution
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = 512;
    canvas.height = 256;

    // Draw transparent black background for better visibility
    context.fillStyle = 'rgba(0, 0, 0, 0.7)';
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Draw text
    context.fillStyle = '#ffffff'; // White text for visibility
    context.font = 'bold 128px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, 256, 128);

    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      depthTest: false,
      depthWrite: false,
      sizeAttenuation: true, // Scale with camera zoom
    });

    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.position.copy(position);

    // Calculate scale based on camera zoom (for orthographic camera)
    const camera = this.viewer.scene.getActiveCamera();
    let scale = 0.3;
    if (camera instanceof THREE.OrthographicCamera) {
      const frustumHeight = camera.top - camera.bottom;
      // Scale based on frustum size - smaller frustum = more zoomed in = smaller label needed
      scale = frustumHeight / this.options.labelScaleDivisor;
    }

    sprite.scale.set(scale, scale / 2, 1); // Maintain 2:1 aspect ratio
    sprite.renderOrder = 1001; // Higher render order to appear on top

    // Store sprite in visuals for cleanup
    this.visuals.angleLabel = sprite;
    this.viewer.scene.scene.add(sprite);
  }

  /**
   * Clear all camera alignment visuals
   */
  private clearVisuals(): void {
    if (this.visuals.startCross) {
      this.viewer.scene.scene.remove(this.visuals.startCross);
      this.visuals.startCross.geometry.dispose();
      (this.visuals.startCross.material as THREE.Material).dispose();
      this.visuals.startCross = undefined;
    }

    if (this.visuals.endCross) {
      this.viewer.scene.scene.remove(this.visuals.endCross);
      this.visuals.endCross.geometry.dispose();
      (this.visuals.endCross.material as THREE.Material).dispose();
      this.visuals.endCross = undefined;
    }

    if (this.visuals.line) {
      this.viewer.scene.scene.remove(this.visuals.line);
      this.visuals.line.geometry.dispose();
      (this.visuals.line.material as THREE.Material).dispose();
      this.visuals.line = undefined;
    }

    if (this.visuals.arc) {
      this.viewer.scene.scene.remove(this.visuals.arc);
      this.visuals.arc.geometry.dispose();
      (this.visuals.arc.material as THREE.Material).dispose();
      this.visuals.arc = undefined;
    }

    if (this.visuals.angleLabel) {
      this.viewer.scene.scene.remove(this.visuals.angleLabel);
      this.visuals.angleLabel.material.map?.dispose();
      this.visuals.angleLabel.material.dispose();
      this.visuals.angleLabel = undefined;
    }
  }

  /**
   * Dispose of the tool and clean up all resources
   */
  public dispose(): void {
    this.cancel();
  }
}
